import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { Images, Link2, Lock, Plus, Printer, ShieldCheck } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { LoginArea } from '@/components/auth/LoginArea';
import { CreateAlbumDialog } from '@/components/CreateAlbumDialog';
import { useMyAlbums } from '@/hooks/useAlbums';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { albumHue, albumNaddr, type AlbumManifest } from '@/lib/album';

const Index = () => {
  useSeoMeta({
    title: 'Locket Album — private encrypted photo & video albums',
    description:
      'End-to-end encrypted photo and video albums on Nostr. The share link is the key — only people with the link can view the album. Print A5 leaflets with QR codes.',
  });

  const { user } = useCurrentUser();
  const [createOpen, setCreateOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="mx-auto flex h-14 max-w-5xl items-center justify-between px-4">
          <span className="flex items-center gap-2 font-semibold">
            <Lock className="size-4" /> Locket Album
          </span>
          <LoginArea className="max-w-40" />
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div
            className="pointer-events-none absolute inset-0 bg-gradient-to-br from-violet-600/15 via-fuchsia-500/10 to-sky-500/15"
            aria-hidden
          />
          <div className="relative mx-auto max-w-5xl px-4 py-20 text-center sm:py-28">
            <div className="mx-auto mb-6 flex size-14 items-center justify-center rounded-2xl bg-violet-600 text-white shadow-lg shadow-violet-600/25">
              <Lock className="size-7" />
            </div>
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
              Private albums, unlocked by a link
            </h1>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground">
              Photos and videos are end-to-end encrypted before they leave your device. The decryption key lives
              inside the share link — only people with the link can view the album. No passwords, no accounts for
              your guests.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              {user ? (
                <Button size="lg" onClick={() => setCreateOpen(true)}>
                  <Plus className="size-4" /> Create encrypted album
                </Button>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <LoginArea className="max-w-60" />
                  <p className="text-xs text-muted-foreground">Log in with Nostr to create an album — viewing never requires an account.</p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="mx-auto max-w-5xl px-4 pb-16">
          <div className="grid gap-4 sm:grid-cols-3">
            <Feature
              icon={<ShieldCheck className="size-5" />}
              title="End-to-end encrypted"
              body="Every photo and video is encrypted on your device with AES-256-GCM. Relays and storage servers only ever see ciphertext."
            />
            <Feature
              icon={<Link2 className="size-5" />}
              title="The link is the key"
              body="The secret key travels in the URL fragment (after #), so it is never sent to any server. Sub-albums derive their own keys from the same link via a salt."
            />
            <Feature
              icon={<Printer className="size-5" />}
              title="Printable leaflets"
              body="Generate a 4-page A5 leaflet with the album cover, ID and a QR code — in a color derived from the album ID, or plain black & white."
            />
          </div>
        </section>

        {/* My albums */}
        {user && (
          <section className="mx-auto max-w-5xl px-4 pb-20">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold">Your albums</h2>
              <Button variant="outline" size="sm" onClick={() => setCreateOpen(true)}>
                <Plus className="size-4" /> New album
              </Button>
            </div>
            <MyAlbums />
          </section>
        )}
      </main>

      <footer className="border-t py-6">
        <p className="text-center text-xs text-muted-foreground">
          Locket Album ·{' '}
          <a href="https://marlowe.shakespeare.wtf" target="_blank" rel="noreferrer" className="hover:underline">
            Vibed with Marlowe
          </a>
        </p>
      </footer>

      <CreateAlbumDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
};

function Feature({ icon, title, body }: { icon: ReactNode; title: string; body: string }) {
  return (
    <Card>
      <CardContent className="space-y-2 p-5">
        <div className="flex size-10 items-center justify-center rounded-lg bg-violet-600/10 text-violet-600 dark:text-violet-400">
          {icon}
        </div>
        <h3 className="font-semibold">{title}</h3>
        <p className="text-sm text-muted-foreground">{body}</p>
      </CardContent>
    </Card>
  );
}

function MyAlbums() {
  const albums = useMyAlbums();
  const navigate = useNavigate();

  if (albums.isPending) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-44 rounded-xl" />
        ))}
      </div>
    );
  }

  if (!albums.data || albums.data.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-12 px-8 text-center">
          <p className="text-muted-foreground max-w-sm mx-auto">
            No albums yet. Create your first encrypted album and share it with a secret link.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {albums.data.map((m) => (
        <AlbumCard key={m.d} manifest={m} onOpen={() => navigate(`/a/${albumNaddr(m.event.pubkey, m.d)}`)} />
      ))}
    </div>
  );
}

function AlbumCard({ manifest, onOpen }: { manifest: AlbumManifest; onOpen: () => void }) {
  const hue = albumHue(manifest.d);
  return (
    <button onClick={onOpen} className="text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-xl">
      <Card className="overflow-hidden transition-shadow hover:shadow-md">
        {manifest.cover ? (
          <img src={manifest.cover} alt="" className="h-28 w-full object-cover" />
        ) : (
          <div
            className="flex h-28 items-center justify-center"
            style={{ backgroundColor: `hsl(${hue} 55% 45%)` }}
          >
            <Images className="size-8 text-white/80" />
          </div>
        )}
        <CardContent className="p-4">
          <h3 className="truncate font-semibold">{manifest.title || 'Untitled album'}</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            {manifest.files.length} {manifest.files.length === 1 ? 'file' : 'files'}
            {manifest.subs.length > 0 && ` · ${manifest.subs.length} sub-album${manifest.subs.length === 1 ? '' : 's'}`}
          </p>
        </CardContent>
      </Card>
    </button>
  );
}

export default Index;
